import { useCallback, useRef, useState } from "react"

import {
  isLookupZip,
  type ZipFill,
  type ZipMatch,
  zipFillFrom
} from "../components/AutoComplete/zip-lookup.ts"
import { autocompleteZip } from "../services/shared/address-autocomplete"
import { useDebouncedCallback } from "./use-debounced-callback.ts"

export type ZipLookupStatus =
  | { kind: "idle" }
  | { kind: "unknown" }
  | { kind: "failed" }
  | { kind: "unsupportedState"; state: string }
  | { kind: "choices"; matches: ZipMatch[] }

interface UseZipLookupOptions {
  onFill: (fill: ZipFill) => void
  knownStates?: string[]
  disabled?: boolean
  /**
   * The ZIP the form opened with, if any. Its city and state came from
   * somewhere, so typing it again must not replace them. A form that starts
   * empty passes nothing.
   */
  initialZip?: string | null
}

/** The address autocomplete's own pacing, so the two controls behave alike. */
const DEBOUNCE_MS = 700

export function useZipLookup({
  onFill,
  knownStates = [],
  disabled,
  initialZip
}: UseZipLookupOptions) {
  const [status, setStatus] = useState<ZipLookupStatus>({ kind: "idle" })
  const appliedZip = useRef<string | null>((initialZip ?? "").trim() || null)
  const pendingZip = useRef<string | null>(null)
  const requestId = useRef(0)
  const armed = useRef(0)

  /**
   * Abandon both halves of a lookup in progress: the keystroke still waiting out
   * the debounce, and the request already on the wire. Either would otherwise
   * land on fields that have since been filled from somewhere else.
   */
  const discardPending = useCallback(() => {
    armed.current++
    requestId.current++
  }, [])

  const apply = useCallback(
    (match: ZipMatch, zip: string) => {
      const fill = zipFillFrom(match, knownStates)
      appliedZip.current = zip
      onFill(fill)
      setStatus(
        fill.state === undefined && match.state
          ? { kind: "unsupportedState", state: match.state }
          : { kind: "idle" }
      )
    },
    [knownStates, onFill]
  )

  const run = useCallback(
    async (value: string, token: number) => {
      if (token !== armed.current) return

      const zip = (value ?? "").trim()

      if (disabled || !isLookupZip(zip)) {
        setStatus({ kind: "idle" })
        return
      }

      // A city the user corrected by hand must survive typing the same ZIP again.
      if (appliedZip.current === zip) return

      const id = ++requestId.current
      pendingZip.current = zip

      let response
      try {
        response = await autocompleteZip({ q: zip })
      } catch {
        response = undefined
      }

      // A slower earlier request must not overwrite a later one's answer.
      if (id !== requestId.current) return

      // A server that is down, offline, or answering a shape we do not know is
      // not the same thing as a ZIP that does not exist, and saying so would tell
      // the user their correct ZIP is invalid.
      const matches = response?.ok ? response.data?.suggestions : undefined
      if (!Array.isArray(matches)) {
        setStatus({ kind: "failed" })
        return
      }

      if (!matches.length) {
        setStatus({ kind: "unknown" })
        return
      }

      if (matches.length === 1) {
        apply(matches[0], zip)
        return
      }

      setStatus({ kind: "choices", matches })
    },
    [apply, disabled]
  )

  const lookupDebounced = useDebouncedCallback(run, DEBOUNCE_MS)

  const lookupZip = useCallback(
    (value: string) => lookupDebounced(value, ++armed.current),
    [lookupDebounced]
  )

  const chooseMatch = useCallback(
    (match: ZipMatch) => apply(match, pendingZip.current ?? match.zip_code),
    [apply]
  )

  /**
   * Record a ZIP filled by something else — choosing a full address sets city,
   * state and ZIP together, and looking that ZIP up would replace a borough with
   * the postal city.
   */
  const suppressLookupFor = useCallback(
    (zip: string | null | undefined) => {
      appliedZip.current = (zip ?? "").trim() || null
      discardPending()
      setStatus({ kind: "idle" })
    },
    [discardPending]
  )

  /** Takes the hint down without forgetting which ZIP the fields reflect. */
  const clearStatus = useCallback(() => {
    discardPending()
    setStatus({ kind: "idle" })
  }, [discardPending])

  return { lookupZip, chooseMatch, suppressLookupFor, clearStatus, status }
}
